import { apiClient } from './client'

export type ModerationTargetType = 'Post' | 'Comment' | 'ImageAsset' | 'User'
export type ModerationReportReason = 'HARASS' | 'HATE' | 'SEX' | 'VIOL' | 'DOXX' | 'SHARM' | 'DANG' | 'SPAM' | 'OTHER'
export type ModerationInterimAction = 'None' | 'SoftHide' | 'HardHide'
export type ModerationQueueType = 'PRIORITY' | 'STANDARD' | 'APPEALS'
export type ModerationCaseStatus = 'Open' | 'Closed'
export type ModerationDecision =
  | 'DISMISS'
  | 'REMOVE'
  | 'REMOVE_STRIKE'
  | 'REMOVE_LOCK'
  | 'SUSPEND_24H'
  | 'SUSPEND_7D'
  | 'BAN'

export type ReporterTrustTier = 'NEW' | 'STANDARD' | 'TRUSTED'

export type ModerationCase = {
  id: string
  targetType: ModerationTargetType
  targetId: string
  targetOwnerUserId?: string | null
  targetOwnerUsername?: string | null
  targetSnippet?: string | null
  status: ModerationCaseStatus
  queue: ModerationQueueType
  interimAction: ModerationInterimAction
  highestReasonCode: ModerationReportReason
  weightedReports24h: number
  reportCount24h: number
  suspectedBrigade: boolean
  assignedToUserId?: string | null
  assignedToName?: string | null
  assignedAt?: string | null
  priorityAlertedAt?: string | null
  createdAt: string
  updatedAt: string
  resolvedAt?: string | null
  resolvedByUserId?: string | null
  decision?: ModerationDecision | null
  decisionNotes?: string | null
  appealedAt?: string | null
  appealDetails?: string | null
}

export type ModerationReportDetail = {
  id: string
  reporterId: string
  reporterUsername?: string | null
  reporterProfileImageUrl?: string | null
  reporterAccountCreatedAt?: string | null
  reporterUpheldReportCount: number
  reporterOverturnedReportCount: number
  trustTier: ReporterTrustTier
  weight: number
  reasonCode: ModerationReportReason
  details?: string | null
  createdAt: string
  lastReportedAt: string
}

export type ModerationTargetPreview = {
  targetType: ModerationTargetType
  targetId: string
  exists: boolean
  content?: string | null
  imageUrls: string[]
  authorUserId?: string | null
  authorUsername?: string | null
  authorProfileImageUrl?: string | null
  createdAt?: string | null
  likeCount?: number | null
  commentCount?: number | null
  activityTitle?: string | null
  parentPostId?: string | null
  interimAction: ModerationInterimAction
  threadLockedAt?: string | null
}

export type ModerationUserSummary = {
  userId: string
  username?: string | null
  fullName?: string | null
  profileImageUrl?: string | null
  bio?: string | null
  accountCreatedAt?: string | null
  strikeCount90d: number
  priorCasesUpheld: number
  priorCasesDismissed: number
  upheldReportCount: number
  overturnedReportCount: number
  postingMutedUntil?: string | null
  suspendedUntil?: string | null
  bannedAt?: string | null
}

export type ModerationCaseDetail = {
  case: ModerationCase
  target: ModerationTargetPreview
  targetOwner?: ModerationUserSummary | null
  reports: ModerationReportDetail[]
}

export type ModerationStaffMember = {
  userId: string
  name: string
  role: string
}

export type ModerationCasesResponse = {
  cases: ModerationCase[]
  hasMore: boolean
  cursor?: string | null
}

export type GetModerationCasesParams = {
  status?: ModerationCaseStatus | 'all'
  queue?: ModerationQueueType | 'all'
  limit?: number
  cursor?: string
}

export async function getModerationCases(params?: GetModerationCasesParams) {
  const searchParams = new URLSearchParams()
  if (params?.status && params.status !== 'all') searchParams.set('status', params.status)
  if (params?.queue && params.queue !== 'all') searchParams.set('queue', params.queue)
  if (params?.limit) searchParams.set('limit', params.limit.toString())
  if (params?.cursor) searchParams.set('cursor', params.cursor)

  const query = searchParams.toString()
  const { data } = await apiClient.get<ModerationCasesResponse>(`/api/admin/moderation/cases${query ? `?${query}` : ''}`)
  return data
}

export async function getModerationCaseDetail(caseId: string) {
  const { data } = await apiClient.get<ModerationCaseDetail>(`/api/admin/moderation/cases/${encodeURIComponent(caseId)}`)
  return data
}

export async function getModerationStaff() {
  const { data } = await apiClient.get<ModerationStaffMember[]>('/api/admin/moderation/staff')
  return data
}

export async function assignModerationCase(caseId: string, assigneeUserId: string | null) {
  const { data } = await apiClient.post<ModerationCase>(
    `/api/admin/moderation/cases/${encodeURIComponent(caseId)}/assign`,
    { assigneeUserId }
  )
  return data
}

export type ResolveModerationCaseRequest = {
  decision: ModerationDecision
  notes?: string | null
  notifyOwner?: boolean
  notifyReporters?: boolean
}

export async function resolveModerationCase(caseId: string, request: ResolveModerationCaseRequest) {
  const { data } = await apiClient.post<ModerationCase>(`/api/admin/moderation/cases/${encodeURIComponent(caseId)}/decision`, request)
  return data
}
